/**
 * Catalogue — the brand's Shopify products and their variants.
 *
 * Product-first, with variants behind a click, because the ratio demands it: the development
 * store is 274 products to 7,813 variants, ~28.5 each. One flat variant table would be 313
 * pages of near-duplicate rows, and Polaris 13.9 has no expandable-row API to group them in
 * place. A modal per product keeps one paginated list on screen and reads the variants only
 * when someone asks for them.
 *
 * Everything here is the MIRROR, not Shopify. It is as current as the last catalogue sync —
 * which is nightly, or whenever someone presses Resync — so the last-imported time is shown
 * next to the counts rather than buried.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Box,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Badge,
  Button,
  Banner,
  Filters,
  Divider,
  IndexTable,
  Pagination,
  EmptyState,
  Modal,
  Thumbnail,
  Spinner,
  ChoiceList,
  SkeletonBodyText,
  SkeletonDisplayText,
  Tooltip,
} from '@shopify/polaris';
import { ImageIcon, ExternalIcon } from '@shopify/polaris-icons';

import apiClient from '../utils/api';
import { headingsFor, emptyRow, DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';
import {
  useCatalogueProducts,
  useCatalogueVariants,
  useCatalogueStatus,
} from '../utils/queries';

/**
 * The row count the PRODUCT table opens on, and now only its seed.
 *
 * That table pages server-side — `limit` goes out with the request and the pager reads the
 * response's own totalPages — so the chosen size has to reach the API. Slicing what came back
 * would show 10 of the 25 rows the server sent under a page count computed from all of them.
 */
const PAGE_SIZE = 25;

/** ACTIVE is the only healthy state; the other two are reasons a SKU is not sellable. */
const STATUS_TONE = { ACTIVE: 'success', DRAFT: 'attention', ARCHIVED: undefined };

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

/**
 * Shopify admin URLs take the NUMERIC id, but we store the gid
 * (gid://shopify/Product/10348371214641) because that is what GraphQL returns. Linking with
 * the gid produces a 404, so the trailing segment is what goes in the URL.
 */
const numericId = (gid) => (gid ? String(gid).split('/').pop() : null);

/**
 * Column definitions for the two tables here. See utils/table.jsx for why an empty table
 * needs a synthetic row to keep its headings.
 */
const PRODUCT_COLUMNS = [
  { key: 'thumb', title: '', hidden: true },
  { key: 'product', title: 'Product', tip: "The product title as it stands in the brand's Shopify store." },
  { key: 'vendor', title: 'Vendor', tip: "Shopify's own vendor field. It is not what a retailer matches on — retailers match the brand name configured per connection." },
  { key: 'type', title: 'Type', tip: "Shopify's product type, mirrored as-is." },
  { key: 'status', title: 'Status', tip: 'Active, draft or archived in Shopify. Only active products are considered listable at a retailer.' },
  { key: 'variants', title: 'Variants', tip: 'How many variants the product has. A variant with no SKU cannot be listed at a retailer at all.' },
  { key: 'open', title: 'Variants', hidden: true },
];

const VARIANT_COLUMNS = [
  { key: 'variant', title: 'Variant', tip: 'The variant title as Shopify holds it.' },
  { key: 'sku', title: 'SKU', tip: 'The SKU a retailer matches on. Blank means this variant cannot be listed anywhere — that is the single most common reason a product never reaches a retailer.' },
  { key: 'price', title: 'Price', tip: "The brand's own Shopify price. A marketplace price is set separately and routinely differs." },
  { key: 'inventory', title: 'Inventory', tip: "Shopify's stock figure as of the last catalogue read, which runs nightly — not live." },
  { key: 'spacer', title: '', hidden: true },
];

export default function ShopifyCatalogueTab({ brandId, brandName, shopDomain }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [page, setPage] = useState(1);
  // Seeded with this table's own existing count, so nobody's view changes size unasked.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [openProduct, setOpenProduct] = useState(null);
  // The variants modal's own paging, kept separate from the product table's: the two tables
  // are read separately, and one shared size would resize a table nobody touched. Its rows
  // arrive in a single 250-row fetch, so that table pages CLIENT-side.
  const [variantPage, setVariantPage] = useState(1);
  const [variantPageSize, setVariantPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  // 300ms, matching OrderManagement — enough to stop a query per keystroke without the
  // field feeling laggy.
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Any change to what is being filtered must return to page 1. Without this, narrowing a
  // filter while on page 9 shows an empty table and looks like missing data.
  // The row count belongs in the same reset: page 9 of a 25-row window is inside page 5 of a
  // 50-row one, so keeping the number asks the API for a page past the end.
  React.useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, pageSize]);

  // Opening a DIFFERENT product is a different list, so its paging starts again — otherwise a
  // 40-variant product read on page 3 opens a 6-variant one on a page that does not exist.
  React.useEffect(() => { setVariantPage(1); }, [openProduct, variantPageSize]);

  const params = useMemo(() => ({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter.length === 1 ? statusFilter[0] : undefined,
  }), [page, pageSize, debouncedSearch, statusFilter]);

  const { data, isLoading, error: queryError } = useCatalogueProducts(brandId, params);
  const { data: status, refetch: refetchStatus } = useCatalogueStatus(brandId);

  // Only fetched once a product is opened — `enabled` keeps it from running otherwise.
  const { data: variantData, isLoading: variantsLoading } = useCatalogueVariants(
    brandId,
    { productId: openProduct?.shopifyProductId, limit: 250 },
    { enabled: !!openProduct },
  );

  const products = data?.products ?? [];
  const pagination = data?.pagination ?? { page: 1, totalPages: 1, totalCount: 0, hasNext: false, hasPrevious: false };

  const handleResync = useCallback(async () => {
    setSyncing(true);
    setError('');
    setNotice('');
    try {
      await apiClient.triggerCatalogueSync(brandId);
      setNotice('Catalogue sync queued. A full pass takes a minute or two — the counts update when it finishes.');
      // A full pass is ~35 Shopify requests, so re-reading immediately would show the old
      // numbers and look like the button did nothing.
      setTimeout(refetchStatus, 15000);
    } catch (err) {
      setError(err.message || 'Failed to queue the catalogue sync');
    }
    setSyncing(false);
  }, [brandId, refetchStatus]);

  const filters = [
    {
      key: 'status',
      label: 'Product status',
      filter: (
        <ChoiceList
          title="Product status"
          titleHidden
          choices={[
            { label: 'Active', value: 'ACTIVE' },
            { label: 'Draft', value: 'DRAFT' },
            { label: 'Archived', value: 'ARCHIVED' },
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = statusFilter.length
    ? [{
      key: 'status',
      label: `Status: ${statusFilter.join(', ')}`,
      onRemove: () => setStatusFilter([]),
    }]
    : [];

  const clearAll = () => { setStatusFilter([]); setSearchQuery(''); };

  const emptyState = (
    <EmptyState
      heading={appliedFilters.length || debouncedSearch ? 'No products match' : 'No catalogue imported yet'}
      image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
      action={appliedFilters.length || debouncedSearch
        ? { content: 'Clear filters', onAction: clearAll }
        : { content: 'Resync catalogue', onAction: handleResync, loading: syncing }}
    >
      <p>
        {appliedFilters.length || debouncedSearch
          ? 'Try a different search term or status.'
          : 'This brand’s Shopify products have not been imported yet. The import runs nightly, or you can start one now.'}
      </p>
    </EmptyState>
  );

  const rows = products.map((p, index) => (
    <IndexTable.Row id={p.id} key={p.id} position={index} onClick={() => setOpenProduct(p)}>
      <IndexTable.Cell>
        {p.featuredImageUrl
          ? <Thumbnail size="small" source={p.featuredImageUrl} alt={p.featuredImageAlt || p.title || 'Product image'} />
          : <Thumbnail size="small" source={ImageIcon} alt="No image" />}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <BlockStack gap="050">
          <Text variant="bodyMd" fontWeight="semibold">{p.title || 'Untitled product'}</Text>
          {p.handle && <Text variant="bodySm" tone="subdued">{p.handle}</Text>}
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell>{p.vendor || '—'}</IndexTable.Cell>
      <IndexTable.Cell>{p.productType || '—'}</IndexTable.Cell>
      <IndexTable.Cell>
        {p.status
          ? <Badge tone={STATUS_TONE[p.status]}>{p.status}</Badge>
          : <Text tone="subdued">—</Text>}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" numeric>{p.variantCount ?? '—'}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {shopDomain && p.shopifyProductId && (
          <Tooltip content="View in Shopify">
            <Button
              variant="tertiary"
              size="micro"
              icon={ExternalIcon}
              url={`https://${shopDomain}/admin/products/${numericId(p.shopifyProductId)}`}
              external
              // The row itself opens the variants modal, so without this the link would
              // navigate AND open the modal behind it. Same guard the orders table uses.
              onClick={(e) => e.stopPropagation()}
              accessibilityLabel={`View ${p.title || 'product'} in Shopify`}
            />
          </Tooltip>
        )}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  if (isLoading && page === 1) {
    return (
      <Card>
        {/* Same 200 as the loaded card, so the skeleton does not shift when content arrives. */}
        <Box padding="200">
          <BlockStack gap="400">
            <SkeletonDisplayText size="medium" />
            <SkeletonBodyText lines={6} />
          </BlockStack>
        </Box>
      </Card>
    );
  }

  const variants = variantData?.variants ?? [];
  // CLAMPED, not trusted. The variant list is refetched per product and can shrink under a page
  // that was valid when it was chosen, and an out-of-range slice is empty — which would render
  // the "no variant recorded" row over a product that has plenty.
  const variantTotalPages = Math.max(1, Math.ceil(variants.length / variantPageSize));
  const variantSafePage = Math.min(variantPage, variantTotalPages);
  const visibleVariants = variants.slice(
    (variantSafePage - 1) * variantPageSize,
    variantSafePage * variantPageSize,
  );

  return (
    <BlockStack gap="400">
      {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
      {notice && <Banner tone="info" onDismiss={() => setNotice('')}>{notice}</Banner>}
      {queryError && (
        <Banner tone="critical">
          Could not load the catalogue: {queryError.message}
        </Banner>
      )}

      {/* Counts, and how current they are. Mirrors the tiles on Retail Connect rather than
          moving them, so neither screen depends on the other being visited first. */}
      <Card>
        {/* No "Shopify Catalogue" heading and no divider under it.

            The heading repeated the tab you are standing on, and with it gone the divider had
            nothing to divide — it was separating a title from the figures it titled. The Resync
            button now sits on the same row as the figures, which is the only thing that was ever
            beside it.

            Padding drops to Card's own 16px + 200 = 24px, matching every other card in this
            page, and the stat label/value pairs go to 050: at 100 the label floated between its
            own number and the one above it. */}
        <Box padding="200">
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="start" gap="400" wrap={false}>
              <InlineGrid columns={{ xs: 2, md: 4 }} gap="400">
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Products</Text>
                <Text variant="headingLg" numeric>{pagination.totalCount}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Variants</Text>
                <Text variant="headingLg" numeric>{status?.variants ?? '—'}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">With a SKU</Text>
                <Text variant="headingLg" numeric>{status?.withSku ?? '—'}</Text>
              </BlockStack>
              <BlockStack gap="050">
                <Text variant="bodySm" tone="subdued">Last imported</Text>
                <Text variant="bodyMd">
                  {status?.lastSyncedAt ? new Date(status.lastSyncedAt).toLocaleString() : 'Never'}
                </Text>
              </BlockStack>
              </InlineGrid>
              <Button onClick={handleResync} loading={syncing}>Resync Catalogue</Button>
            </InlineStack>
            {status && status.variants > status.withSku && (
              <Text variant="bodySm" tone="subdued">
                {status.variants - status.withSku} variant(s) have no SKU. Those cannot be listed at a
                retailer and are excluded from every SKU count.
              </Text>
            )}
          </BlockStack>
        </Box>
      </Card>

      <Card>
        <Box padding="200">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search by product title, vendor, type or handle..."
            onQueryChange={setSearchQuery}
            onQueryClear={() => setSearchQuery('')}
            filters={filters}
            appliedFilters={appliedFilters}
            onClearAll={clearAll}
            disabled={isLoading}
          />
        </Box>
        <Divider />
        <IndexTable
          resourceName={{ singular: 'product', plural: 'products' }}
          // The server's total, not rows.length — otherwise the header reports the page size.
          // The server's total, not rows.length — otherwise the header reports the page
          // size. But 1 when there are no rows at all, so IndexTable still renders the
          // header row: at itemCount 0 it swaps the whole table for `emptyState`, headings
          // included, which is why that prop is no longer used. The empty state itself is
          // unchanged and still carries its action — it has just moved inside the table.
          itemCount={rows.length ? (pagination.totalCount || rows.length) : 1}
          headings={headingsFor(PRODUCT_COLUMNS)}
          loading={isLoading}
          selectable={false}
        >
          {rows.length === 0 ? (
            <IndexTable.Row id="empty-products" position={0}>
              <IndexTable.Cell colSpan={PRODUCT_COLUMNS.length}>{emptyState}</IndexTable.Cell>
            </IndexTable.Row>
          ) : rows}
        </IndexTable>
        {/* Both controls in one row. The pager drops out when everything fits, but the row
            count stays: it is how someone GETS a second page, so hiding it at size 50 would
            strand them there. Neither is shown over an empty table, where the empty state is
            already carrying its own action. */}
        {rows.length > 0 && (
          <Box padding="400">
            <InlineStack align="space-between" blockAlign="center">
              <PageSizeSelect value={pageSize} onChange={setPageSize} />
              {pagination.totalPages > 1 && (
                <Pagination
                  hasPrevious={pagination.hasPrevious}
                  onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                  hasNext={pagination.hasNext}
                  onNext={() => setPage((p) => p + 1)}
                  label={`Page ${pagination.page} of ${pagination.totalPages}`}
                />
              )}
            </InlineStack>
          </Box>
        )}
      </Card>

      {openProduct && (
        <Modal
          open
          onClose={() => setOpenProduct(null)}
          title={openProduct.title || 'Product variants'}
          secondaryActions={[{ content: 'Close', onAction: () => setOpenProduct(null) }]}
          large
        >
          <Modal.Section>
            {variantsLoading ? (
              <InlineStack align="center"><Spinner /></InlineStack>
            ) : (
              <BlockStack gap="300">
                <Text variant="bodySm" tone="subdued">
                  {variants.length} variant(s){openProduct.vendor ? ` · ${openProduct.vendor}` : ''}
                </Text>
                <IndexTable
                  resourceName={{ singular: 'variant', plural: 'variants' }}
                  // Follows the array actually rendered, and forced to 1 when that is empty so
                  // IndexTable keeps its heading row instead of swapping the whole table out.
                  itemCount={visibleVariants.length || 1}
                  headings={headingsFor(VARIANT_COLUMNS)}
                  selectable={false}
                >
                  {visibleVariants.length === 0 ? emptyRow(
                    VARIANT_COLUMNS, 'variants',
                    'No variant recorded for this product. Nothing on it can be listed at a retailer until the catalogue import picks one up.',
                  ) : visibleVariants.map((v, i) => (
                    <IndexTable.Row id={v.id} key={v.id} position={i}>
                      <IndexTable.Cell>{v.variantTitle || 'Default'}</IndexTable.Cell>
                      <IndexTable.Cell>
                        {/* A Badge rather than a dash: a missing SKU is actionable, and this
                            is the only screen where those variants are visible at all. */}
                        {v.sku
                          ? <Text variant="bodyMd" fontWeight="semibold">{v.sku}</Text>
                          : <Badge tone="attention">No SKU</Badge>}
                      </IndexTable.Cell>
                      <IndexTable.Cell><Text numeric>{money(v.price)}</Text></IndexTable.Cell>
                      <IndexTable.Cell><Text numeric>{v.inventoryQuantity ?? '—'}</Text></IndexTable.Cell>
                      <IndexTable.Cell>
                        {shopDomain && v.shopifyProductId && v.shopifyVariantId && (
                          <Tooltip content="View variant in Shopify">
                            <Button
                              variant="tertiary"
                              size="micro"
                              icon={ExternalIcon}
                              url={`https://${shopDomain}/admin/products/${numericId(v.shopifyProductId)}/variants/${numericId(v.shopifyVariantId)}`}
                              external
                              accessibilityLabel={`View variant ${v.sku || v.variantTitle || ''} in Shopify`}
                            />
                          </Tooltip>
                        )}
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
                {/* The product with the most variants in the dev store has hundreds; this
                    table had no pager at all, so the modal was as tall as the list. Labelled
                    with `variantSafePage`, the value the rows were actually sliced with. */}
                {variants.length > 0 && (
                  <InlineStack align="space-between" blockAlign="center">
                    <PageSizeSelect value={variantPageSize} onChange={setVariantPageSize} />
                    {variantTotalPages > 1 && (
                      <Pagination
                        hasPrevious={variantSafePage > 1}
                        onPrevious={() => setVariantPage(variantSafePage - 1)}
                        hasNext={variantSafePage < variantTotalPages}
                        onNext={() => setVariantPage(variantSafePage + 1)}
                        label={`Page ${variantSafePage} of ${variantTotalPages}`}
                      />
                    )}
                  </InlineStack>
                )}
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>
      )}
    </BlockStack>
  );
}
